import React, { useState, useRef, useEffect } from 'react';
import type { VideoScene } from './videoStudioTypes';
import { trimVideo } from './videoTrimmer';
import { videoStudioApi } from './videoStudioApi';

interface VideoTrimmerModalProps {
  campaignId: string;
  creativeId: string;
  scene: VideoScene | null;
  aspectRatio?: string;
  onClose: () => void;
  onSuccess: (videoUrl?: string) => void;
}

export const VideoTrimmerModal: React.FC<VideoTrimmerModalProps> = ({
  campaignId,
  creativeId,
  scene,
  aspectRatio = '9:16',
  onClose,
  onSuccess,
}) => {
  const [videoUrl, setVideoUrl] = useState<string>('');
  const [file, setFile] = useState<File | null>(null);
  const [duration, setDuration] = useState<number>(0);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [startTime, setStartTime] = useState<number>(0);
  const [endTime, setEndTime] = useState<number>(5);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [progressMsg, setProgressMsg] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [format, setFormat] = useState<'mp4' | 'webm'>('mp4');

  const videoRef = useRef<HTMLVideoElement>(null);
  const startCanvasRef = useRef<HTMLCanvasElement>(null);
  const endCanvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (scene?.video_url) {
      setVideoUrl(scene.video_url);
    } else {
      setVideoUrl('');
    }
    const defaultDuration = scene?.duration_seconds || 5;
    setStartTime(0);
    setEndTime(defaultDuration);
    setErrorMessage(null);
  }, [scene]);

  const updateSnapshot = (time: number, canvas: HTMLCanvasElement | null) => {
    if (!videoRef.current || !canvas) return;
    const v = document.createElement('video');
    v.crossOrigin = 'anonymous';
    v.src = videoRef.current.src;
    v.muted = true;
    v.playsInline = true;
    v.currentTime = Math.max(0, time);
    v.onseeked = () => {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
      }
      v.removeAttribute('src');
      v.load();
    };
  };

  const handleLoadedMetadata = () => {
    if (!videoRef.current) return;
    const d = videoRef.current.duration || 0;
    setDuration(d);
    const targetDur = scene?.duration_seconds || 5;
    const initialEnd = Math.min(d > 0 ? d : targetDur, targetDur);
    setEndTime(initialEnd);
    updateSnapshot(0, startCanvasRef.current);
    updateSnapshot(initialEnd, endCanvasRef.current);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) {
      setFile(f);
      const url = URL.createObjectURL(f);
      setVideoUrl(url);
      setStartTime(0);
      setEndTime(scene?.duration_seconds || 5);
    }
  };

  const adjustStartTime = (delta: number) => {
    const next = Math.max(0, Math.min(endTime - 0.1, parseFloat((startTime + delta).toFixed(1))));
    setStartTime(next);
    updateSnapshot(next, startCanvasRef.current);
    if (videoRef.current) videoRef.current.currentTime = next;
  };

  const adjustEndTime = (delta: number) => {
    const next = Math.min(duration || 999, Math.max(startTime + 0.1, parseFloat((endTime + delta).toFixed(1))));
    setEndTime(next);
    updateSnapshot(next, endCanvasRef.current);
    if (videoRef.current) videoRef.current.currentTime = next;
  };

  const handleSetStartHere = () => {
    if (!videoRef.current) return;
    const raw = videoRef.current.currentTime;
    const t = Math.max(0, parseFloat((raw > 0.15 ? raw - 0.15 : raw).toFixed(1)));
    setStartTime(t);
    if (t >= endTime) setEndTime(Math.min(duration, t + (scene?.duration_seconds || 5)));
    updateSnapshot(t, startCanvasRef.current);
  };

  const handleSetEndHere = () => {
    if (!videoRef.current) return;
    const t = Math.max(0.1, parseFloat(videoRef.current.currentTime.toFixed(1)));
    if (t <= startTime) {
      setStartTime(Math.max(0, t - 1));
    }
    setEndTime(t);
    updateSnapshot(t, endCanvasRef.current);
  };

  const previewSegment = (seekTo: number) => {
    if (!videoRef.current) return;
    videoRef.current.currentTime = seekTo;
    videoRef.current.play().catch(() => {});
  };

  const handleTrimAndApply = async () => {
    if (!scene) return;
    if (endTime <= startTime) {
      setErrorMessage('O fim do recorte deve ser maior que o início.');
      return;
    }
    setIsProcessing(true);
    setErrorMessage(null);
    setProgressMsg('Iniciando recorte...');

    try {
      const source = file || videoUrl;
      const trimmedBlob = await trimVideo(source, startTime, endTime, {
        aspectRatio,
        format,
        onProgress: (p) => setProgressMsg(p),
      });

      setProgressMsg('Enviando trecho para a cena...');
      const rawMime = trimmedBlob.type || 'video/mp4';
      const cleanMime = rawMime.split(';')[0].trim().toLowerCase();
      const ext = cleanMime.includes('webm') ? 'webm' : 'mp4';
      const finalMime = cleanMime.includes('webm') ? 'video/webm' : 'video/mp4';

      const trimmedFile = new File([trimmedBlob], `scene_${scene.scene_order}_trimmed.${ext}`, {
        type: finalMime,
      });

      const res = await videoStudioApi.uploadSceneVideo(campaignId, creativeId, scene.scene_order, trimmedFile);
      if (!res.ok) {
        throw new Error(res.message || 'Falha ao salvar trecho');
      }

      setProgressMsg('Trecho aplicado com sucesso!');
      setTimeout(() => {
        setIsProcessing(false);
        onSuccess(res.video_url);
        onClose();
      }, 500);
    } catch (err: any) {
      setIsProcessing(false);
      setErrorMessage(err.message || 'Falha ao recortar vídeo.');
    }
  };

  if (!scene) return null;
  const cutDuration = (endTime - startTime).toFixed(1);

  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.85)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
      <div style={{ backgroundColor: '#18181b', border: '1px solid #3f3f46', borderRadius: '12px', width: '100%', maxWidth: '780px', maxHeight: '90vh', overflowY: 'auto', padding: '24px', color: '#f4f4f5' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>✂️ Editor de Recorte de Vídeo</span>
              <span style={{ fontSize: '12px', background: '#3b82f620', color: '#60a5fa', padding: '2px 8px', borderRadius: '4px' }}>Cena #{scene.scene_order}</span>
            </h3>
            <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#a1a1aa' }}>
              Selecione o início e fim exatos com preview instantâneo dos frames para aproveitar o trecho desejado.
            </p>
          </div>
          <button onClick={onClose} disabled={isProcessing} style={{ background: 'transparent', border: 'none', color: '#a1a1aa', fontSize: '20px', cursor: 'pointer' }}>✕</button>
        </div>

        <div style={{ marginBottom: '14px', display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          <input type="file" ref={fileInputRef} accept="video/*" style={{ display: 'none' }} onChange={handleFileChange} />
          <button onClick={() => fileInputRef.current?.click()} disabled={isProcessing} style={{ background: 'linear-gradient(135deg, #0284c7, #0369a1)', border: 'none', color: '#fff', fontWeight: 600, borderRadius: '6px', padding: '6px 14px', fontSize: '12px', cursor: 'pointer', boxShadow: '0 2px 6px rgba(2, 132, 199, 0.3)' }}>
            📁 {file ? `Alterar: ${file.name}` : 'Carregar Vídeo do Computador'}
          </button>
          {scene.video_url && (
            <button onClick={() => { setFile(null); setVideoUrl(scene.video_url!); }} disabled={isProcessing || !file} style={{ background: '#27272a', border: '1px solid #52525b', color: '#d4d4d8', borderRadius: '6px', padding: '6px 12px', fontSize: '12px', cursor: 'pointer' }}>
              Restaurar Vídeo da Cena
            </button>
          )}
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '12px', color: '#a1a1aa' }}>Formato:</span>
            <button type="button" onClick={() => setFormat('mp4')} style={{ padding: '4px 10px', borderRadius: '4px', fontSize: '11px', fontWeight: 700, border: 'none', cursor: 'pointer', background: format === 'mp4' ? '#10b981' : '#27272a', color: format === 'mp4' ? '#fff' : '#a1a1aa' }}>MP4</button>
            <button type="button" onClick={() => setFormat('webm')} style={{ padding: '4px 10px', borderRadius: '4px', fontSize: '11px', fontWeight: 700, border: 'none', cursor: 'pointer', background: format === 'webm' ? '#10b981' : '#27272a', color: format === 'webm' ? '#fff' : '#a1a1aa' }}>WebM</button>
          </div>
        </div>

        {videoUrl ? (
          <div>
            <div style={{ display: 'flex', gap: '16px', background: '#09090b', padding: '12px', borderRadius: '8px', border: '1px solid #27272a' }}>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <video
                  ref={videoRef}
                  src={videoUrl}
                  crossOrigin="anonymous"
                  controls
                  playsInline
                  onLoadedMetadata={handleLoadedMetadata}
                  onTimeUpdate={() => videoRef.current && setCurrentTime(videoRef.current.currentTime)}
                  style={{ maxHeight: '260px', width: '100%', objectFit: 'contain', borderRadius: '6px', background: '#000' }}
                />
                <div style={{ fontSize: '11px', color: '#a1a1aa', marginTop: '6px', width: '100%', display: 'flex', justifyContent: 'space-between' }}>
                  <span>Atual: {currentTime.toFixed(1)}s</span>
                  <span>Total: {duration.toFixed(1)}s</span>
                </div>
              </div>

              <div style={{ width: '220px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ background: '#18181b', border: '1px solid #27272a', borderRadius: '6px', padding: '8px', textAlign: 'center' }}>
                  <div style={{ fontSize: '11px', color: '#60a5fa', fontWeight: 600, marginBottom: '4px' }}>Frame de Início ({startTime.toFixed(1)}s)</div>
                  <canvas ref={startCanvasRef} width={120} height={160} style={{ width: '100%', height: '80px', objectFit: 'contain', background: '#000', borderRadius: '4px' }} />
                  <button onClick={() => previewSegment(startTime)} style={{ marginTop: '4px', width: '100%', background: 'linear-gradient(135deg, #1d4ed8, #2563eb)', border: 'none', color: '#ffffff', fontWeight: 600, borderRadius: '4px', padding: '4px', fontSize: '11px', cursor: 'pointer' }}>
                    ▶ Ver Início
                  </button>
                </div>

                <div style={{ background: '#18181b', border: '1px solid #27272a', borderRadius: '6px', padding: '8px', textAlign: 'center' }}>
                  <div style={{ fontSize: '11px', color: '#34d399', fontWeight: 600, marginBottom: '4px' }}>Frame de Fim ({endTime.toFixed(1)}s)</div>
                  <canvas ref={endCanvasRef} width={120} height={160} style={{ width: '100%', height: '80px', objectFit: 'contain', background: '#000', borderRadius: '4px' }} />
                  <button onClick={() => previewSegment(Math.max(startTime, endTime - 1.5))} style={{ marginTop: '4px', width: '100%', background: 'linear-gradient(135deg, #059669, #10b981)', border: 'none', color: '#ffffff', fontWeight: 600, borderRadius: '4px', padding: '4px', fontSize: '11px', cursor: 'pointer' }}>
                    ▶ Ver Final
                  </button>
                </div>
              </div>
            </div>

            <div style={{ marginTop: '16px', background: '#27272a40', padding: '14px', borderRadius: '8px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '4px' }}>
                    <span style={{ color: '#60a5fa', fontWeight: 500 }}>Início do Trecho:</span>
                    <span>{startTime.toFixed(1)}s</span>
                  </div>
                  <input type="range" min={0} max={Math.max(0.1, duration)} step={0.1} value={startTime} onChange={(e) => { const v = parseFloat(e.target.value); setStartTime(v); updateSnapshot(v, startCanvasRef.current); }} style={{ width: '100%' }} />
                  <div style={{ display: 'flex', gap: '4px', marginTop: '4px' }}>
                    <button type="button" onClick={() => adjustStartTime(-0.2)} style={{ flex: 1, background: '#27272a', border: '1px solid #3f3f46', color: '#93c5fd', borderRadius: '4px', padding: '3px 0', fontSize: '11px', cursor: 'pointer' }}>⏪ -0.2s</button>
                    <button type="button" onClick={() => adjustStartTime(0.2)} style={{ flex: 1, background: '#27272a', border: '1px solid #3f3f46', color: '#93c5fd', borderRadius: '4px', padding: '3px 0', fontSize: '11px', cursor: 'pointer' }}>+0.2s ⏩</button>
                  </div>
                  <button onClick={handleSetStartHere} style={{ marginTop: '4px', width: '100%', background: 'linear-gradient(135deg, #2563eb, #1d4ed8)', border: 'none', color: '#ffffff', fontWeight: 600, borderRadius: '4px', padding: '5px 8px', fontSize: '11px', cursor: 'pointer', boxShadow: '0 2px 6px rgba(37,99,235,0.3)' }}>
                    📍 Fixar Início na Posição Atual
                  </button>
                </div>

                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '4px' }}>
                    <span style={{ color: '#34d399', fontWeight: 500 }}>Fim do Trecho:</span>
                    <span>{endTime.toFixed(1)}s</span>
                  </div>
                  <input type="range" min={0.1} max={Math.max(0.1, duration)} step={0.1} value={endTime} onChange={(e) => { const v = parseFloat(e.target.value); setEndTime(v); updateSnapshot(v, endCanvasRef.current); }} style={{ width: '100%' }} />
                  <div style={{ display: 'flex', gap: '4px', marginTop: '4px' }}>
                    <button type="button" onClick={() => adjustEndTime(-0.2)} style={{ flex: 1, background: '#27272a', border: '1px solid #3f3f46', color: '#6ee7b7', borderRadius: '4px', padding: '3px 0', fontSize: '11px', cursor: 'pointer' }}>⏪ -0.2s</button>
                    <button type="button" onClick={() => adjustEndTime(0.2)} style={{ flex: 1, background: '#27272a', border: '1px solid #3f3f46', color: '#6ee7b7', borderRadius: '4px', padding: '3px 0', fontSize: '11px', cursor: 'pointer' }}>+0.2s ⏩</button>
                  </div>
                  <button onClick={handleSetEndHere} style={{ marginTop: '4px', width: '100%', background: 'linear-gradient(135deg, #10b981, #059669)', border: 'none', color: '#ffffff', fontWeight: 600, borderRadius: '4px', padding: '5px 8px', fontSize: '11px', cursor: 'pointer', boxShadow: '0 2px 6px rgba(16,185,129,0.3)' }}>
                    📍 Fixar Fim na Posição Atual
                  </button>
                </div>
              </div>

              <div style={{ fontSize: '11px', color: '#94a3b8', fontStyle: 'italic', background: '#1e293b40', padding: '4px 8px', borderRadius: '4px' }}>
                💡 <strong>Prevenção de corte:</strong> Fixar início já adiciona 0.15s de folga para não cortar a primeira sílaba. Use os botões ±0.2s para ajuste milimétrico.
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #3f3f46', paddingTop: '10px' }}>
                <span style={{ fontSize: '13px', color: '#e4e4e7' }}>
                  Duração resultante: <strong style={{ color: '#38bdf8' }}>{cutDuration}s</strong> (Recomendado da cena: {scene.duration_seconds || 5}s)
                </span>
                <button onClick={() => { if (videoRef.current) { videoRef.current.currentTime = startTime; videoRef.current.play(); } }} style={{ background: 'linear-gradient(135deg, #6366f1, #4f46e5)', border: 'none', color: '#ffffff', fontWeight: 600, borderRadius: '4px', padding: '5px 12px', fontSize: '11px', cursor: 'pointer', boxShadow: '0 2px 6px rgba(99,102,241,0.25)' }}>
                  ▶ Tocar Trecho
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px 16px', background: '#09090b', borderRadius: '8px', border: '1px dashed #3f3f46', color: '#a1a1aa' }}>
            Nenhum vídeo carregado. Carregue um arquivo de vídeo do seu computador para recortar e aplicar a esta cena.
          </div>
        )}

        {errorMessage && (
          <div style={{ marginTop: '12px', padding: '8px 12px', background: '#ef444420', border: '1px solid #ef4444', borderRadius: '6px', color: '#f87171', fontSize: '12px' }}>
            ⚠️ {errorMessage}
          </div>
        )}

        {isProcessing && (
          <div style={{ marginTop: '12px', padding: '10px', background: '#3b82f620', border: '1px solid #3b82f6', borderRadius: '6px', color: '#93c5fd', fontSize: '13px', textAlign: 'center' }}>
            ⏳ {progressMsg}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
          <button onClick={onClose} disabled={isProcessing} style={{ background: '#27272a', border: '1px solid #3f3f46', color: '#d4d4d8', borderRadius: '6px', padding: '8px 16px', fontSize: '13px', cursor: 'pointer' }}>
            Cancelar
          </button>
          <button onClick={handleTrimAndApply} disabled={isProcessing || !videoUrl} style={{ background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)', border: 'none', color: '#fff', fontWeight: 700, borderRadius: '6px', padding: '8px 20px', fontSize: '13px', cursor: isProcessing || !videoUrl ? 'not-allowed' : 'pointer', opacity: isProcessing || !videoUrl ? 0.6 : 1, boxShadow: '0 3px 12px rgba(109,40,217,0.35)' }}>
            {isProcessing ? 'Processando Recorte...' : `✂️ Recortar e Salvar na Cena #${scene.scene_order}`}
          </button>
        </div>
      </div>
    </div>
  );
};
